/**
 * Générateur — Additionner deux nombres décimaux relatifs.
 * Attendu ID : 916 | Niveau : 5ème | Domaine : Nombres relatifs
 *
 * Libellé officiel (spécification 916.json) : « Additionner deux nombres
 * décimaux relatifs. »
 *
 * Plan de cartes (version conforme à la spécification, campagne 5e 2026-09-22) :
 *
 *   Carte « somme » — Additionner deux nombres relatifs (niveaux 1 à 5)
 *     texte_trou_saisie : la somme, l'écriture de l'énoncé étant tirée dans la
 *     liste fermée (tous les termes parenthésés / seuls les négatifs / écriture
 *     simplifiée) ;
 *     qcm_unique : EXACTEMENT trois distracteurs, le signe faux sur le premier
 *     terme, sur le second, sur les deux — ils couvrent toutes les erreurs de
 *     signe ; un tirage qui les ferait coïncider est rejoué ;
 *     tableau_vf : quatre lignes tirées parmi six gabarits à prédicat variable.
 *
 *   Carte « trou » — L'addition à trou (niveau 6)
 *     texte_trou_saisie : le terme manquant, la position du trou tournant
 *     entre le premier et le second terme ;
 *     qcm_unique : l'opposé du terme connu et la somme des deux données en
 *     distracteurs.
 *
 *   Carte « serie » — La série de sommes (dispositif de répétition)
 *     texte_trou_saisie : 6 à 9 sommes, une seule à deux termes positifs
 *     (au plus une question sur six), les trois autres couples de signes
 *     tous présents.
 *
 * Spécification (916.json) :
 *   Bornes appliquées : nombre_de_termes = 2 exactement (`origine: prescrit`) ;
 *     nombre_de_decimales_de_chaque_terme 0..2 appliqué à CHAQUE terme, le cas
 *     « deux décimales sur les deux termes » existant (niveau 5) ;
 *     premier_terme_relatif et second_terme_relatif -20..20, aucun terme nul ;
 *     repartition_des_signes = les quatre couples, (+,+) au plus une fois sur
 *     six (une fois sur dix dans les formats à une question, exactement une
 *     ligne sur 6 à 9 dans la série) ;
 *     forme_d_ecriture_de_l_enonce = les trois écritures de la liste fermée,
 *     tirées par question ;
 *     tache_demandee = calculer la somme ET trouver le terme manquant ;
 *     nombre_de_questions_de_la_serie 4..10 (6 à 9 pour la carte « serie »).
 *   Exclusions : aucun terme nul ; aucune somme nulle construite (zéro peut
 *     sortir du tirage, il n'est jamais imposé) ; pas plus d'une question sur
 *     six à deux termes positifs ; aucune imbrication de parenthèses ni
 *     troisième terme ; aucune proposition de QCM en double (le tirage est
 *     rejoué, jamais réduit) ; aucun libellé annonçant « entiers relatifs »
 *     alors que le générateur produit des décimaux.
 *   Erreurs visées → mots-clés (_meta.params.distracteurs[].code,
 *   _meta.params.lignes[].code, erreurVisee) :
 *     « Appliquer la règle des signes de la multiplication à une addition »
 *         → regle_des_signes_de_la_multiplication (somme_qcm_unique, somme_vf,
 *           trou_qcm_unique)
 *     « Additionner les distances à zéro puis donner le signe du premier terme »
 *         → distances_ajoutees_signe_du_premier (somme_vf)
 *     « Donner au résultat le signe du terme écrit en premier »
 *         → signe_du_premier_terme (somme_vf, somme_texte_trou_saisie)
 *     « Se tromper de signe sur le seul second terme, ou sur le seul premier »
 *         → signe_faux_sur_un_terme (somme_qcm_unique ×2, trou_qcm_unique)
 *     « Aligner mal les rangs décimaux quand les deux termes n'ont pas le même
 *       nombre de décimales » → rangs_decimaux_mal_alignes (somme_vf,
 *       somme_texte_trou_saisie et serie quand les deux termes diffèrent)
 *     « Dans une addition à trou, chercher l'opposé du terme connu »
 *         → oppose_au_lieu_de_la_difference (trou ×2, somme_vf)
 *     « Écrire deux signes côte à côte faute de parenthèse »
 *         → deux_signes_cote_a_cote (somme_vf)
 *   Graduation → cartes :
 *     niveau 1 (deux entiers de signes contraires) et niveau 2 (deux entiers,
 *       les quatre couples) → carte somme, tirages entiers ;
 *     niveau 3 (un terme à une décimale, l'autre entier) → tirage (1, 0) ;
 *     niveau 4 (les deux à une décimale) → tirage (1, 1) ;
 *     niveau 5 (les deux à deux décimales) → tirages (2, 2) et (2, 1) ;
 *     niveau 6 (addition à trou, les deux positions) → carte trou.
 *   Non tranché : amplitude_maximale_reglable (10..100) n'a pas de mécanisme de
 *     réglage et contredit les bornes des deux termes (-20..20) ; le tableau à
 *     double entrée d'objets_geometriques n'a pas de format de saisie en
 *     tableau dans l'application.
 *
 * Arithmétique en CENTIÈMES entiers. Signe « - » = tiret clavier partout.
 */

import {
    randomChoice, randInt, shuffle,
    makeQcm, makeVraiFaux, makeTexteTrou,
} from '../../../utils';

const ATTENDU_ID = 916;

const L_SOMME = 'Additionner deux nombres relatifs';
const L_TROU = 'L\'addition à trou';
const L_SERIE = 'La série de sommes';

const opts = (label) => ({ niveau: '5eme', matiere: 'maths', attendu: ATTENDU_ID, label });

/** Centièmes entiers → écriture française. cent(0) = « 0 ». */
const cent = (c) => {
    const s = c < 0 ? '-' : '';
    const a = Math.abs(c);
    const ent = Math.floor(a / 100);
    const r = a % 100;
    if (r === 0) return `${s}${ent}`;
    if (r % 10 === 0) return `${s}${ent},${r / 10}`;
    return `${s}${ent},${String(r).padStart(2, '0')}`;
};
/** Écriture signée : le + est écrit devant un positif ; 0 n'est jamais signé. */
const signe = (c) => (c === 0 ? '0' : c < 0 ? cent(c) : `+${cent(c)}`);

const CONSIGNE_RES = 'Écris le résultat en écriture décimale ; un résultat positif peut s\'écrire avec ou sans le signe +.';
const CONSIGNE_TROU = 'Écris le nombre manquant sans parenthèses ; un nombre positif peut s\'écrire avec ou sans le signe +.';

// ═════════ Tirage des termes ═════════

/** Un terme non nul, de valeur absolue ≤ 20, avec exactement `dec` décimales. */
const tirerTerme = (dec) => {
    if (dec === 0) return randInt(1, 20) * 100;
    if (dec === 1) {
        let k = randInt(1, 199);
        if (k % 10 === 0) k += 1;
        return k * 10;
    }
    let k = randInt(1, 1999);
    if (k % 10 === 0) k += 1;
    return k;
};

/**
 * Les couples de décimales par niveau de la graduation. Le cas « deux
 * décimales sur les deux termes » existe (niveau 5), et le cas où les deux
 * termes n'ont pas le même nombre de décimales aussi (erreur visée 5).
 */
const DECIMALES_PAR_NIVEAU = {
    1: [0, 0], 2: [0, 0], 3: [1, 0], 4: [1, 1], 5: [2, 2],
};
const NIVEAUX_SOMME = [1, 2, 3, 3, 4, 4, 5, 5];

/**
 * repartition_des_signes : les quatre couples, (+,+) une fois sur dix — au
 * plus une question sur six, dit l'exclusion.
 */
const SIGNES = ['pn', 'pn', 'pn', 'np', 'np', 'np', 'nn', 'nn', 'nn', 'pp'];
const SIGNES_CONTRAIRES = ['pn', 'np'];

const appliquerSignes = (cas, x, y) => {
    if (cas === 'pp') return [x, y];
    if (cas === 'nn') return [-x, -y];
    return cas === 'pn' ? [x, -y] : [-x, y];
};

/** Un couple de termes selon le niveau ; la position de la décimale tourne. */
const tirerTermes = (niveau, casSignes) => {
    const [d1, d2] = DECIMALES_PAR_NIVEAU[niveau];
    const [da, db] = randomChoice([[d1, d2], [d2, d1]]);
    return appliquerSignes(casSignes, tirerTerme(da), tirerTerme(db));
};

// ═════════ Écriture de l'énoncé (liste fermée de la spec) ═════════

const FORMES = ['tous', 'negatifs', 'simplifiee'];

const ecrireSomme = (a, b, forme) => {
    if (forme === 'tous') return `(${signe(a)}) + (${signe(b)})`;
    if (forme === 'negatifs') {
        const g = a < 0 ? `(${cent(a)})` : cent(a);
        const d = b < 0 ? `(${cent(b)})` : cent(b);
        return `${g} + ${d}`;
    }
    return `${cent(a)} ${b < 0 ? '-' : '+'} ${cent(Math.abs(b))}`;
};

/** L'écriture d'une somme dont un terme est remplacé par un trou. */
const ecrireSommeTrou = (a, b, forme, position) => {
    const trou = (v) => `{{${cent(v)}}}`;
    if (forme === 'tous') {
        return position === 0
            ? `(${trou(a)}) + (${signe(b)})`
            : `(${signe(a)}) + (${trou(b)})`;
    }
    if (forme === 'negatifs') {
        const g = position === 0 ? trou(a) : (a < 0 ? `(${cent(a)})` : cent(a));
        const d = position === 1 ? trou(b) : (b < 0 ? `(${cent(b)})` : cent(b));
        return `${g} + ${d}`;
    }
    // Écriture simplifiée : le signe de l'opération est celui du second terme,
    // le trou ne porte donc jamais ce signe.
    return position === 0
        ? `${trou(a)} ${b < 0 ? '-' : '+'} ${cent(Math.abs(b))}`
        : `${cent(a)} + ${trou(b)}`;
};

/** Décalage des rangs décimaux : 2,5 devient 2,05 (erreur visée 5). */
const malAligne = (c) => {
    const s = c < 0 ? -1 : 1;
    const a = Math.abs(c);
    return s * (Math.floor(a / 100) * 100 + Math.floor((a % 100) / 10));
};

/** Tire nb vérités avec au moins une vraie et une fausse. */
const tirerVerites = (nb) => {
    const v = [];
    for (let i = 0; i < nb; i++) v.push(randomChoice([true, false]));
    if (v.every(x => x)) v[randInt(0, nb - 1)] = false;
    else if (v.every(x => !x)) v[randInt(0, nb - 1)] = true;
    return v;
};

// ═════════ Carte « somme » ═════════

const genSommeSaisie = () => {
    const niveau = randomChoice(NIVEAUX_SOMME);
    const cas = randomChoice(niveau === 1 ? SIGNES_CONTRAIRES : SIGNES);
    const [a, b] = tirerTermes(niveau, cas);
    const forme = randomChoice(FORMES);
    const phrase = `${ecrireSomme(a, b, forme)} = {{${cent(a + b)}}}`;
    const payload = makeTexteTrou(phrase,
        `<p><b>Effectue le calcul.</b> ${CONSIGNE_RES}</p>`,
        { ...opts(L_SOMME), mode: 'saisie' });
    payload._meta.params = {
        a, b, forme, cas, niveau,
        erreurVisee: (a % 100 === 0) !== (b % 100 === 0)
            ? 'rangs_decimaux_mal_alignes'
            : 'signe_du_premier_terme',
    };
    return payload;
};

const genSommeQcm = () => {
    const niveau = randomChoice(NIVEAUX_SOMME);
    const cas = randomChoice(niveau === 1 ? SIGNES_CONTRAIRES : SIGNES);
    let [a, b] = tirerTermes(niveau, cas);
    // Les trois distracteurs prescrits coïncident quand la somme est nulle ou
    // quand les deux termes sont égaux : le tirage est REJOUÉ, jamais réduit.
    for (let essai = 0; essai < 40 && (a + b === 0 || a === b); essai++) {
        [a, b] = tirerTermes(niveau, cas);
    }
    if (a + b === 0 || a === b) b += b < 0 ? -100 : 100; // repli déterministe
    const forme = randomChoice(FORMES);
    const distracteurs = [
        { valeur: cent(-a + b), code: 'signe_faux_sur_un_terme' },
        { valeur: cent(a - b), code: 'signe_faux_sur_un_terme' },
        { valeur: cent(-a - b), code: 'regle_des_signes_de_la_multiplication' },
    ];
    const payload = makeQcm([cent(a + b), ...distracteurs.map(d => d.valeur)], cent(a + b),
        `<p><b>Que vaut ${ecrireSomme(a, b, forme)} ?</b></p>`,
        opts(L_SOMME));
    payload._meta.params = { a, b, forme, cas, niveau, distracteurs };
    return payload;
};

/**
 * Gabarits à prédicat variable : chacun produit une ligne VRAIE ou FAUSSE
 * selon `v`. ctx : { n1, n2 (deux négatifs), p, q (contraires, |p| ≠ |q|),
 * x (une décimale), y (deux décimales), t1, t2 (addition à trou) }.
 */
const GABARITS_SOMME = [
    {
        id: 'regle_signes', code: 'regle_des_signes_de_la_multiplication',
        ligne: (v, c) => `(${signe(c.n1)}) + (${signe(c.n2)}) = ${cent(v ? c.n1 + c.n2 : -(c.n1 + c.n2))}.`,
    },
    {
        id: 'signe_du_plus_grand', code: 'signe_du_premier_terme',
        ligne: (v, c) => `(${signe(c.p)}) + (${signe(c.q)}) = ${cent(v ? c.p + c.q : -(c.p + c.q))}.`,
    },
    {
        id: 'distances', code: 'distances_ajoutees_signe_du_premier',
        ligne: (v, c) => `Pour calculer (${signe(c.p)}) + (${signe(c.q)}), on calcule `
            + `${v ? 'la différence' : 'la somme'} des distances à zéro des deux termes.`,
    },
    {
        id: 'parenthese', code: 'deux_signes_cote_a_cote',
        ligne: (v, c) => `L'écriture ${cent(Math.abs(c.p))} + ${v ? `(${cent(-Math.abs(c.q))})` : cent(-Math.abs(c.q))} est correcte.`,
    },
    {
        id: 'alignement', code: 'rangs_decimaux_mal_alignes',
        ligne: (v, c) => `${cent(c.x)} + ${cent(c.y)} = ${cent(v ? c.x + c.y : malAligne(c.x) + c.y)}.`,
    },
    {
        id: 'trou', code: 'oppose_au_lieu_de_la_difference',
        ligne: (v, c) => `Pour compléter ${cent(c.t1)} + … = ${cent(c.t2)}, on calcule `
            + `${cent(c.t2)} ${v ? '-' : '+'} ${cent(c.t1)}.`,
    },
];

const genSommeVf = () => {
    const n1 = -tirerTerme(randomChoice([0, 1]));
    let n2 = -tirerTerme(randomChoice([0, 1]));
    if (n2 === n1) n2 -= 100; // repli déterministe
    const p = tirerTerme(randomChoice([0, 1]));
    let q = -tirerTerme(randomChoice([0, 1]));
    // |p| ≠ |q| : la somme n'est jamais nulle, les deux formes du gabarit diffèrent
    for (let essai = 0; essai < 20 && Math.abs(q) === p; essai++) q = -tirerTerme(randomChoice([0, 1]));
    if (Math.abs(q) === p) q = -(p > 100 ? p - 100 : p + 100); // repli déterministe
    const x = tirerTerme(1);
    const y = tirerTerme(2);
    const t1 = randomChoice([1, -1]) * tirerTerme(0);
    let t2 = randomChoice([1, -1]) * tirerTerme(0);
    if (t2 === t1) t2 += 100;
    const ctx = { n1, n2, p, q, x, y, t1, t2 };
    const gabarits = shuffle(GABARITS_SOMME).slice(0, 4);
    const verites = tirerVerites(4);
    const lignes = gabarits.map((g, i) => ({
        template: g.id, code: g.code, vrai: verites[i], texte: g.ligne(verites[i], ctx),
    }));
    const payload = makeVraiFaux(
        lignes.map(l => l.texte),
        lignes.map(l => (l.vrai ? 0 : 1)),
        '<p><b>Pour chaque affirmation, coche Vrai ou Faux.</b></p>',
        opts(L_SOMME));
    payload._meta.params = { ...ctx, niveau: 5, lignes };
    return payload;
};

// ═════════ Carte « trou » — L'addition à trou (niveau 6) ═════════

const genTrouSaisie = () => {
    const niveau = randomChoice(NIVEAUX_SOMME);
    const cas = randomChoice(SIGNES);
    const [a, b] = tirerTermes(niveau, cas);
    const forme = randomChoice(FORMES);
    const position = randInt(0, 1);
    const phrase = `${ecrireSommeTrou(a, b, forme, position)} = ${cent(a + b)}`;
    const payload = makeTexteTrou(phrase,
        `<p><b>Complète l'égalité.</b> ${CONSIGNE_TROU}</p>`,
        { ...opts(L_TROU), mode: 'saisie' });
    payload._meta.params = {
        a, b, forme, cas, position, niveau: 6,
        erreurVisee: 'oppose_au_lieu_de_la_difference',
    };
    return payload;
};

const genTrouQcm = () => {
    const niveau = randomChoice(NIVEAUX_SOMME);
    const cas = randomChoice(SIGNES);
    const position = randInt(0, 1);
    // Trois propositions coïncideraient si la somme était nulle, si les deux
    // termes étaient égaux, ou si la somme et le terme connu étaient opposés :
    // le tirage est REJOUÉ, jamais réduit à moins de propositions.
    const invalide = (x, y) => {
        const connuLocal = position === 0 ? y : x;
        return x + y === 0 || x === y || x + y + connuLocal === 0;
    };
    let [a, b] = tirerTermes(niveau, cas);
    for (let essai = 0; essai < 60 && invalide(a, b); essai++) [a, b] = tirerTermes(niveau, cas);
    if (invalide(a, b)) { a = 300; b = -1100; } // repli déterministe vérifié à la main
    const somme = a + b;
    const connu = position === 0 ? b : a;
    const manquant = position === 0 ? a : b;
    const distracteurs = [
        { valeur: cent(-manquant), code: 'oppose_au_lieu_de_la_difference' },
        { valeur: cent(somme + connu), code: 'signe_faux_sur_un_terme' },
        { valeur: cent(-somme - connu), code: 'regle_des_signes_de_la_multiplication' },
    ];
    const forme = randomChoice(FORMES);
    const payload = makeQcm([cent(manquant), ...distracteurs.map(d => d.valeur)], cent(manquant),
        `<p><b>Quel nombre complète l'égalité ${ecrireSommeTrou(a, b, forme, position).replace(/\{\{[^}]*\}\}/, '…')} = ${cent(somme)} ?</b></p>`,
        opts(L_TROU));
    payload._meta.params = { a, b, forme, cas, position, manquant, niveau: 6, distracteurs };
    return payload;
};

// ═════════ Carte « serie » — La série de sommes ═════════

const genSerie = () => {
    // nombre_de_questions_de_la_serie : 6 à 9, pour que l'unique ligne à deux
    // termes positifs pèse au plus une question sur six.
    const n = randInt(6, 9);
    const cas = shuffle([
        'pp',
        ...Array.from({ length: n - 1 }, (_, i) => ['pn', 'np', 'nn'][i % 3]),
    ]);
    const lignes = cas.map((c) => {
        const niveau = randomChoice(NIVEAUX_SOMME);
        const [a, b] = tirerTermes(niveau, c);
        const forme = randomChoice(FORMES);
        return { a, b, cas: c, forme, texte: `${ecrireSomme(a, b, forme)} = {{${cent(a + b)}}}` };
    });
    const payload = makeTexteTrou(lignes.map(l => l.texte).join('\n'),
        `<p><b>Effectue chaque calcul.</b> ${CONSIGNE_RES} Chaque case compte pour un point.</p>`,
        { ...opts(L_SERIE), mode: 'saisie' });
    payload._meta.params = {
        lignesSerie: lignes, n, niveau: 2,
        erreurVisee: 'rangs_decimaux_mal_alignes',
    };
    return payload;
};

// ─── Registre ────────────────────────────────────────────────
const GENERATORS = {
    somme_texte_trou_saisie: genSommeSaisie,
    somme_qcm_unique: genSommeQcm,
    somme_vf: genSommeVf,
    trou_texte_trou_saisie: genTrouSaisie,
    trou_qcm_unique: genTrouQcm,
    serie_texte_trou_saisie: genSerie,
};

const FORMAT_LABELS = {
    texte_trou_saisie: 'Saisie trous', qcm_unique: 'QCM', vf: 'Vrai/Faux',
};
const buildFormats = (prefix) =>
    Object.keys(GENERATORS).filter(k => k.startsWith(`${prefix}_`))
        .map(k => ({ key: k, label: FORMAT_LABELS[k.slice(prefix.length + 1)] || k }));

export const CATEGORIES = [
    { id: 'somme', label: L_SOMME, formatsList: buildFormats('somme') },
    { id: 'trou', label: L_TROU, formatsList: buildFormats('trou') },
    { id: 'serie', label: L_SERIE, formatsList: buildFormats('serie') },
];

export const generate = (typeExercice) => {
    let key;
    if (typeExercice && GENERATORS[typeExercice]) key = typeExercice;
    else if (typeExercice) {
        const matching = Object.keys(GENERATORS).filter(k => k.startsWith(typeExercice) || k.includes(typeExercice));
        key = matching.length ? randomChoice(matching) : randomChoice(Object.keys(GENERATORS));
    } else key = randomChoice(Object.keys(GENERATORS));
    const payload = GENERATORS[key]();
    payload._meta.generatorKey = key;
    return payload;
};
